<template>
  <div v-if="years?.length" class="documents-list">
    <div
      v-for="year in years"
      :key="year"
      :ref="
        (el) => {
          accordionsRefs[year] = el;
        }
      "
    >
      <ExAccordion
        header-color="secondary"
        header-text-color="white-600"
        background-color="secondary-100"
        :item-id="year"
        :opened-item-id="openedAccordion"
        @update-state="(value) => updateAccordion(value)"
      >
        <template #headerContent>
          <span class="row items-center full-width q-pr-md">
            {{ year }}
            <QSpace />
            <div v-if="year === openedAccordion">
              <a
                v-if="pagination && hasMoreThanOnePage && showCheckboxes"
                class="text-underline text-body1"
                @click.stop="emit('selectWholeYearItems', year)"
              >
                {{ t("CORE.DOCUMENTS.@SELECT_COUNT", documents[year]?.total) }}
              </a>
              <QChip v-if="documents[year]?.total" :label="documents[year].total" color="secondary-light" dense text-color="white" />
            </div>
          </span>
        </template>
        <template #content>
          <div v-if="loading">
            <QSkeleton type="rect" />
          </div>
          <div v-else-if="documents[year] && pageItems.length" class="q-mb-md">
            <div class="documents-list-heading">
              <QCheckbox
                v-if="showCheckboxes"
                :model-value="props.areAllCurrentYearPageItemsSelected"
                color="secondary"
                @update:model-value="(value) => toggleSelectAll(value, pageItems)"
              />
              <div class="documents-list-heading-right" :style="{ '--component-width': colWidth }">
                <span>{{ t("CORE.DOCUMENTS.@DATE") }}</span>
                <span>{{ t("CORE.DOCUMENTS.@DOCUMENT") }}</span>
                <span v-if="nbColumns > 2">{{ t("CORE.DOCUMENTS.@AMOUNT") }}</span>
              </div>
            </div>
            <DocumentListItem
              v-for="item in pageItems"
              :key="item.id"
              :app="app"
              :document="item"
              :nb-columns="nbColumns"
              :show-checkboxes="showCheckboxes"
              :checked="selected.includes(item.id)"
              @update:checked="(value) => emit('toggleDocument', value, item.id)"
            />
          </div>
          <div v-else class="q-mb-md text-center">{{ t("CORE.DOCUMENTS.@NO_DOCUMENTS") }}</div>
          <div v-if="pagination && hasMoreThanOnePage" class="q-px-md flex flex-center">
            <ExPagination
              v-if="documents[year].total > pageSize"
              :default-page="documents[year].page"
              :total="documents[year].total"
              :items-per-page="pageSize"
              class="q-my-md"
              color="secondary"
              :sibling-count="2"
              :show-first-last-buttons="false"
              :show-prev-next-buttons="false"
              :max="PaginationHelpers.getPageNumber(documents[year].total, pageSize)"
              @update:page="(value) => emit('resetSelectedItems', value)"
            />
          </div>
        </template>
      </ExAccordion>
    </div>
  </div>
  <div v-else-if="loading">
    <QSkeleton type="rect" class="q-mb-xs" />
    <QSkeleton type="rect" class="q-mb-xs" />
    <QSkeleton type="rect" />
  </div>
  <div v-else class="q-my-md text-center">
    <NoDocument />
  </div>
</template>

<script setup>
  import DocumentListItem from "@core/components/common/documents/DocumentListItem.component.vue";
  import NoDocument from "@core/components/common/documents/NoDocument.component.vue";
  import { ExAccordion, ExPagination } from "@extracadabra/ui-kit";
  import { useI18n } from "vue-i18n";
  import { PaginationHelpers } from "@core/helpers";
  import { computed, ref } from "vue";
  import { ScrollManager } from "@core/managers/scroll.manager";

  const { t } = useI18n();

  const colWidth = computed(() => {
    return `${100 / props.nbColumns}%`;
  });

  const accordionsRefs = ref({});

  const props = defineProps({
    app: {
      type: String,
      required: true,
    },
    documents: {
      type: Object,
      default: () => ({}),
    },
    currentYear: {
      type: Number,
      default: null,
    },
    pageItems: {
      type: Array,
      default: () => [],
    },
    years: {
      type: Array,
      default: () => [],
    },
    selected: {
      type: Array,
      default: () => [],
    },
    openedAccordion: {
      type: Number,
      default: null,
    },
    areAllCurrentYearPageItemsSelected: {
      type: Boolean,
      default: false,
    },
    hasMoreThanOnePage: {
      type: Boolean,
      default: false,
    },
    showCheckboxes: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Boolean,
      default: false,
    },
    pageSize: {
      type: Number,
      default: 24,
    },
    nbColumns: {
      type: Number,
      default: 2,
    },
  });

  const emit = defineEmits(["selectWholePageItems", "selectWholeYearItems", "toggleDocument", "toggleAccordion", "resetSelectedItems"]);

  const updateAccordion = (value) => {
    emit("toggleAccordion", value);
    scrollToAccordionTop(value);
  };

  const toggleSelectAll = (value, items) => {
    emit(
      "selectWholePageItems",
      value,
      items.map((item) => item.id),
    );
  };

  const scrollToAccordionTop = (year) => {
    // if accordion is open, we scroll vertically to this element
    if (year !== props.currentYear) {
      const { top } = accordionsRefs.value[year].getBoundingClientRect();
      ScrollManager.setVerticalScrollPosition({ position: top, withDelay: true });
    } else {
      // otherwise scroll to the top of the list
      ScrollManager.setVerticalScrollPosition({ position: 0, withDelay: true });
    }
  };
</script>

<style scoped lang="scss">
  .documents-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;
    &-heading {
      display: flex;
      flex-direction: row;
      &-right {
        display: flex;
        align-items: flex-start;
        flex: 1;
        line-height: 1.25rem;
        margin-right: 49px;
        margin-left: 0;
        @media screen and (min-width: $breakpoint-xs) {
          align-items: center;
        }
        > span {
          width: var(--component-width);
          text-align: center;
        }
      }
    }
  }
</style>
